"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { BaseModal } from "@/components/ui/modal";
import { BrandLogo } from "@/components/ui/BrandLogo";
import {
  MONEY_BACK_DAYS,
  PLAN_CONFIG,
  PLAN_FEATURES,
  formatUsd,
  planRank,
  type FeatureValue,
  type PaidPlan,
} from "@/lib/billing/planConfig";
import type { BillingState } from "@/lib/billing/types";
import { useBilling } from "@/features/billing/hooks/useBilling";
import { formatBillingDate } from "@/features/billing/lib/format";
import { PLAN_ACCENT } from "@/features/billing/components/planStyle";
import { useUpgradeSuccessModal } from "../hooks/useUpgradeSuccessModal";
import { useUser } from "../hooks/useUser";

/** The webhook usually lands within seconds of the redirect; give it 40. */
const POLL_EVERY_MS = 2500;
const POLL_TRIES = 16;

const MONO = "font-[family-name:var(--font-geist-mono)]";

/** Shorter names for the diff's left column; the values come from PLAN_FEATURES as-is. */
const SHORT_NAME: Record<string, string> = {
  "Regional top-sellers matrix": "Regional matrix",
  "CSV export of any chart": "CSV export",
  "Launch forecast": "Forecast",
  "Audience insights": "Audience scans",
};

type UnlockedRow = { name: string; value: string };

/** A tier's value as one line of the list; a row is only listed when the tier has it. */
function show(v: Exclude<FeatureValue, false>): string {
  return v === true ? "Included" : v;
}

/**
 * What this purchase unlocked: every row of the pricing table whose value
 * differs between the old tier and the new one, as the new tier has it. Same
 * source as /pricing, so the modal can't promise anything the pricing page
 * doesn't.
 */
export function unlockedRows(from: "free" | PaidPlan, to: PaidPlan): UnlockedRow[] {
  return PLAN_FEATURES.flatMap((f) => {
    const value = f[to];
    if (value === false || value === f[from]) return [];
    return [{ name: SHORT_NAME[f.name] ?? f.name, value: show(value) }];
  });
}

export function UpgradeSuccessModal() {
  const { isOpen, plan, from, openCount, close } = useUpgradeSuccessModal();
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={close}
      maxWidth="max-w-[560px]"
      labelledBy="upgrade-success-title"
      panelClassName="max-h-[92vh] overflow-y-auto"
    >
      {/* Keyed per open, so a second purchase starts a fresh activation poll. */}
      <SuccessBody key={openCount} isOpen={isOpen} plan={plan} from={from} close={close} />
    </BaseModal>
  );
}

function SuccessBody({
  isOpen,
  plan,
  from,
  close,
}: {
  isOpen: boolean;
  plan: PaidPlan;
  from: "free" | PaidPlan;
  close: () => void;
}) {
  const { user, plan: currentPlan, refresh } = useUser();
  const { load } = useBilling();
  const active = planRank(currentPlan) >= planRank(plan);
  const [tries, setTries] = useState(0);
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [billingDone, setBillingDone] = useState(false);
  const accent = PLAN_ACCENT[plan];
  const label = PLAN_CONFIG[plan].label;
  const rows = unlockedRows(from, plan);

  // Returning from checkout the payment is done but our copy of the plan may
  // not be yet (Creem's webhook writes it). Don't congratulate a user who is
  // still locked out — poll until the plan reads as upgraded.
  useEffect(() => {
    if (!isOpen || active || tries >= POLL_TRIES) return;
    const t = setTimeout(() => {
      void refresh().finally(() => setTries((n) => n + 1));
    }, POLL_EVERY_MS);
    return () => clearTimeout(t);
  }, [isOpen, active, tries, refresh]);

  // Once active, read what Creem bills so the footer shows the real renewal.
  useEffect(() => {
    if (!isOpen || !active) return;
    let cancelled = false;
    void load({ force: true })
      .then((s) => {
        if (!cancelled) setBilling(s);
      })
      .catch(() => null)
      .finally(() => {
        if (!cancelled) setBillingDone(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, active, load]);

  const slow = !active && tries >= POLL_TRIES;

  return (
    <div>
      <div className={`flex items-center justify-between ${MONO} text-[11px] uppercase tracking-[0.14em] text-[#5f6b76]`}>
        <span className="flex items-center gap-2">
          <BrandLogo variant="icon" height={20} />
          steampeek
        </span>
        <span>{active ? "plan changed" : "payment received"}</span>
      </div>

      <h2 id="upgrade-success-title" className="mt-6 text-[28px] font-semibold leading-tight tracking-tight text-white">
        {active ? (
          <>
            You&apos;re on <span style={{ color: accent }}>{label}</span>.
          </>
        ) : (
          "Payment received."
        )}
      </h2>

      {active ? (
        <p className="mt-2 text-sm text-[#8f98a0]">Here&apos;s what&apos;s unlocked.</p>
      ) : slow ? (
        <p className="mt-2 text-sm leading-relaxed text-[#8f98a0]" role="status">
          Activation is taking longer than usual. {label} switches on by itself within a few minutes, so refresh
          the page then. Still locked after that? Write to{" "}
          <a href="mailto:support@steampeek.app" className="text-[#66c0f4] hover:underline">
            support@steampeek.app
          </a>
          .
        </p>
      ) : (
        <p className="mt-2 text-sm text-[#8f98a0]" role="status">
          Switching your account to {label}
          <span aria-hidden className="inline-block w-5 animate-pulse">
            …
          </span>
        </p>
      )}

      {/* Line items, like a receipt: dimmed until the plan is actually live. */}
      <dl
        className={`mt-6 border-y border-[#1e2c3b] ${MONO} text-[12.5px] transition-opacity duration-500 ${
          active ? "opacity-100" : "opacity-35"
        }`}
        aria-label={`What ${label} unlocks`}
      >
        {rows.map((r) => (
          <div
            key={r.name}
            className="flex items-baseline justify-between gap-6 border-b border-[#16222f] py-2.5 last:border-b-0"
          >
            <dt className="shrink-0 uppercase tracking-[0.08em] text-[#6b7785]">{r.name}</dt>
            <dd className="min-w-0 text-right text-[#e8eef3]" style={active ? { color: accent } : undefined}>
              {r.value}
            </dd>
          </div>
        ))}
      </dl>

      {active && <BillingLine plan={plan} billing={billing} loading={!billingDone} email={user?.email ?? null} />}

      <div className="mt-7 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        {active ? (
          <Link href="/account" onClick={close} className="text-center text-sm text-[#8f98a0] hover:text-white sm:text-left">
            Account &amp; billing →
          </Link>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={close}
          className="rounded-lg px-5 py-2.5 text-sm font-semibold text-[#0d151e] transition-[filter] hover:brightness-110"
          style={{ backgroundColor: active ? accent : "#2a3a4a", color: active ? "#0d151e" : "#c7d5e0" }}
        >
          {active ? "Start exploring" : "Close"}
        </button>
      </div>
    </div>
  );
}

/**
 * One quiet line from the live billing state. Comped plans have no Creem
 * subscription, and a Creem hiccup leaves `subscription` null — both fall back
 * to what we know instead of inventing a renewal.
 */
function BillingLine({
  plan,
  billing,
  loading,
  email,
}: {
  plan: PaidPlan;
  billing: BillingState | null;
  loading: boolean;
  email: string | null;
}) {
  const sub = billing?.subscription ?? null;
  if (loading && !sub) {
    return <div aria-hidden className="mt-4 h-3 w-2/3 animate-pulse rounded bg-[#16222f]" />;
  }
  // Two lines: what you'll be charged, then where the paperwork went.
  const terms: string[] = [];
  const paper: string[] = [];
  if (billing?.complimentary) {
    terms.push(`${PLAN_CONFIG[plan].label} · complimentary`);
  } else {
    if (sub?.interval) terms.push(sub.interval === "year" ? "Billed yearly" : "Billed monthly");
    if (sub?.nextChargeAt && !sub.cancelAtPeriodEnd) {
      terms.push(
        `renews ${formatBillingDate(sub.nextChargeAt)}${sub.priceCents != null ? ` at ${formatUsd(sub.priceCents)} plus any tax` : ""}`,
      );
    }
    if (email) paper.push(`Receipt sent to ${email} by Creem`);
    paper.push(`${MONEY_BACK_DAYS}-day money-back on the first payment`);
  }
  return (
    <div className={`mt-4 space-y-1 ${MONO} text-[11.5px] leading-relaxed text-[#6b7785] [overflow-wrap:anywhere]`}>
      {terms.length > 0 && <p>{terms.join(" · ")}</p>}
      {paper.length > 0 && <p>{paper.join(" · ")}</p>}
    </div>
  );
}
